import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { simpleChatApi } from "@/lib/api";
import { useChatSse, type AdminChatMessage } from "@/hooks/useChatSse";

/**
 * SimpleChatContext owns chat state, the streaming pipeline, and the admin
 * SSE subscription for the home-page LLM/RAG bot. The provider is mounted
 * once in PublicLayout so a single live session is shared across surfaces.
 *
 * The bot is fully agent-driven now: lead capture happens via the agent's
 * `submit_lead` tool when the LLM has gathered enough context. There are no
 * stage-aware buttons or progress phases on the client — the only UI states
 * are: empty (no messages), active (streaming), and completed (lead sent).
 */

export interface ChatMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
    /** True while the assistant message is still being streamed in. */
    streaming?: boolean;
    /**
     * When the stream errored, carries the original user prompt so the inline
     * "try again" link can re-submit it. Cleared on successful retry.
     */
    error?: { originalText: string };
}

interface SimpleChatState {
    sessionId: string | null;
    messages: ChatMessage[];
    isLoading: boolean;
    completed: boolean;
}

interface SimpleChatContextValue extends SimpleChatState {
    onTextSubmit: (text: string) => Promise<void>;
    onRetry: (originalText: string, errorMessageId: string) => Promise<void>;
    onEndChat: () => Promise<void>;
}

const INITIAL_STATE: SimpleChatState = {
    sessionId: null,
    messages: [],
    isLoading: false,
    completed: false,
};

const SimpleChatContext = createContext<SimpleChatContextValue | null>(null);

interface ProviderProps {
    children: ReactNode;
}

export const SimpleChatProvider = ({ children }: ProviderProps) => {
    const [state, setState] = useState<SimpleChatState>(INITIAL_STATE);

    /**
     * Promise tracking the in-flight `MAIN_MENU → FREE_CHAT` transition. The
     * backend defaults new sessions to MAIN_MENU; the LLM agent only handles
     * the FREE_CHAT stage. Submissions made before this resolves are awaited
     * so they always land in the right routing path.
     */
    const transitionRef = useRef<Promise<void> | null>(null);

    const startNewSession = useCallback(async () => {
        setState({ ...INITIAL_STATE, isLoading: true });
        try {
            const response = await simpleChatApi.start();
            const data = response.data?.success ? response.data.data : null;
            if (!data) throw new Error("Invalid /web/start response");

            setState({
                sessionId: data.sessionId,
                messages: [],
                isLoading: false,
                completed: false,
            });

            // Transition to FREE_CHAT in the background. The user can start
            // typing immediately; onTextSubmit awaits this ref before sending.
            transitionRef.current = simpleChatApi
                .action(data.sessionId, "free_chat")
                .then(() => undefined)
                .catch((err) => {
                    console.warn("free_chat transition failed:", err);
                });
        } catch (error) {
            console.error("Failed to start chat:", error);
            setState({ ...INITIAL_STATE, isLoading: false });
        }
    }, []);

    useEffect(() => {
        void startNewSession();
    }, [startNewSession]);

    const handleAdminMessage = useCallback((message: AdminChatMessage) => {
        setState((prev) => ({
            ...prev,
            messages: [
                ...prev.messages,
                {
                    id: message.id,
                    role: "assistant",
                    content: message.content,
                    timestamp: new Date(message.createdAt),
                },
            ],
        }));
    }, []);

    useChatSse({
        sessionId: state.sessionId,
        onAdminMessage: handleAdminMessage,
        enabled: !!state.sessionId,
    });

    const streamReply = useCallback(
        async (
            sessionId: string,
            text: string,
            userMessageId: string,
            assistantMessageId: string,
        ) => {
            const appendDelta = (delta: string) => {
                setState((prev) => ({
                    ...prev,
                    messages: prev.messages.map((m) =>
                        m.id === assistantMessageId
                            ? { ...m, content: m.content + delta }
                            : m,
                    ),
                }));
            };

            const finalize = (
                finalContent: string,
                opts: { completed?: boolean; isError?: boolean } = {},
            ) => {
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    completed: opts.completed ? true : prev.completed,
                    messages: prev.messages.map((m) =>
                        m.id === assistantMessageId
                            ? {
                                ...m,
                                content: finalContent,
                                streaming: false,
                                error: opts.isError
                                    ? { originalText: text }
                                    : undefined,
                            }
                            : m,
                    ),
                }));
            };

            let receivedDone = false;
            try {
                if (transitionRef.current) {
                    await transitionRef.current;
                    transitionRef.current = null;
                }

                await simpleChatApi.messageStream(sessionId, text, (event) => {
                    if (event.type === "chunk") {
                        appendDelta(event.delta);
                    } else if (event.type === "done") {
                        receivedDone = true;
                        finalize(event.message, {
                            completed: event.completed === true,
                        });
                    } else if (event.type === "error") {
                        receivedDone = true;
                        finalize(event.message, { isError: true });
                    }
                });

                if (!receivedDone) {
                    finalize("", { isError: true });
                }
            } catch (error) {
                console.error("Stream failed:", error);
                finalize("", { isError: true });
            }

            // Suppress unused-var lint for userMessageId — it's part of the
            // function signature for future per-message correlation use.
            void userMessageId;
        },
        [],
    );

    const onTextSubmit = useCallback(
        async (text: string) => {
            const trimmed = text.trim();
            if (!trimmed || !state.sessionId) return;

            const userId = `u-${Date.now()}`;
            const assistantId = `a-${Date.now() + 1}`;

            setState((prev) => ({
                ...prev,
                isLoading: true,
                messages: [
                    ...prev.messages,
                    {
                        id: userId,
                        role: "user",
                        content: trimmed,
                        timestamp: new Date(),
                    },
                    {
                        id: assistantId,
                        role: "assistant",
                        content: "",
                        timestamp: new Date(),
                        streaming: true,
                    },
                ],
            }));

            await streamReply(state.sessionId, trimmed, userId, assistantId);
        },
        [state.sessionId, streamReply],
    );

    const onRetry = useCallback(
        async (originalText: string, errorMessageId: string) => {
            if (!state.sessionId) return;
            // Reuse the existing assistant bubble: clear its error/content and
            // mark it streaming again. This keeps the conversation timeline
            // visually stable instead of stacking duplicate failed bubbles.
            setState((prev) => ({
                ...prev,
                isLoading: true,
                messages: prev.messages.map((m) =>
                    m.id === errorMessageId
                        ? {
                            ...m,
                            content: "",
                            streaming: true,
                            error: undefined,
                        }
                        : m,
                ),
            }));
            await streamReply(state.sessionId, originalText, "", errorMessageId);
        },
        [state.sessionId, streamReply],
    );

    const onEndChat = useCallback(async () => {
        await startNewSession();
    }, [startNewSession]);

    // Preserve the legacy `open-chat` event so callers like the Projects page
    // can scroll the hero chat into view.
    useEffect(() => {
        const onOpenChat = () => {
            const target = document.getElementById("hero-chat");
            if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        };
        window.addEventListener("open-chat", onOpenChat);
        return () => window.removeEventListener("open-chat", onOpenChat);
    }, []);

    const value: SimpleChatContextValue = {
        ...state,
        onTextSubmit,
        onRetry,
        onEndChat,
    };

    return (
        <SimpleChatContext.Provider value={value}>
            {children}
        </SimpleChatContext.Provider>
    );
};

export const useSimpleChat = (): SimpleChatContextValue => {
    const ctx = useContext(SimpleChatContext);
    if (!ctx) {
        throw new Error("useSimpleChat must be used inside <SimpleChatProvider>");
    }
    return ctx;
};
